import { gsap } from "../../animations/gsapSetup.ts";
import { useGsapScrollTrigger } from "../../hooks/useGsapScrollTrigger.ts";
import { useIsMobile } from "../../hooks/useMediaQuery.ts";
import { useReducedMotion } from "../../hooks/useReducedMotion.ts";
import { useI18n } from "../../i18n/context.tsx";
import styles from "./RestSuggestion.module.css";

/* ── Badge type → CSS class mapping ── */
const BADGE_CLASS: Record<string, string> = {
  alert: styles.badgeAlert,
  warning: styles.badgeWarning,
  insight: styles.badgeInsight,
};

/* ── Badge type → icon ── */
const BADGE_ICON: Record<string, string> = {
  alert: "\u{1F6A8}",
  warning: "\u{26A0}\u{FE0F}",
  insight: "\u{1F4A1}",
};

export function RestSuggestion() {
  const { t } = useI18n();
  const reducedMotion = useReducedMotion();
  const isMobile = useIsMobile();

  const badgeLabelMap: Record<string, string> = {
    alert: t.aiTeam.restAlertBadge,
    warning: t.aiTeam.restWarningBadge,
    insight: t.aiTeam.restInsightBadge,
  };

  const ref = useGsapScrollTrigger(
    (el) => {
      if (reducedMotion) return;

      /* Header fade in */
      const header = el.querySelector("[data-rest-header]");
      if (header) {
        gsap.set(header, { y: 30, opacity: 0 });
        gsap.to(header, {
          y: 0,
          opacity: 1,
          duration: 0.6,
          ease: "power3.out",
          scrollTrigger: {
            trigger: el,
            start: "top 80%",
            toggleActions: "play none none reverse",
          },
        });
      }

      /* Cards: fade-up stagger */
      const cards = el.querySelectorAll("[data-rest-card]");
      if (cards.length > 0) {
        if (isMobile) {
          gsap.set(cards, { y: 30, opacity: 0 });
          gsap.to(cards, {
            y: 0,
            opacity: 1,
            duration: 0.4,
            stagger: 0.08,
            ease: "power2.out",
            scrollTrigger: {
              trigger: el,
              start: "top 70%",
              toggleActions: "play none none reverse",
            },
          });
        } else {
          gsap.set(cards, { y: 50, opacity: 0 });
          gsap.to(cards, {
            y: 0,
            opacity: 1,
            duration: 0.5,
            stagger: 0.12,
            ease: "power3.out",
            scrollTrigger: {
              trigger: el,
              start: "top 70%",
              toggleActions: "play none none reverse",
            },
          });
        }
      }

      /* Badge scale pop */
      const badges = el.querySelectorAll("[data-rest-badge]");
      if (badges.length > 0 && !isMobile) {
        gsap.set(badges, { scale: 0, opacity: 0 });
        gsap.to(badges, {
          scale: 1,
          opacity: 1,
          duration: 0.35,
          stagger: 0.12,
          ease: "back.out(3)",
          scrollTrigger: {
            trigger: el,
            start: "top 65%",
            toggleActions: "play none none reverse",
          },
        });
      }

      /* Load bars fill animation */
      const bars = el.querySelectorAll("[data-rest-bar]");
      if (bars.length > 0) {
        gsap.set(bars, { scaleX: 0, transformOrigin: "left center" });
        gsap.to(bars, {
          scaleX: 1,
          duration: 0.6,
          stagger: 0.1,
          ease: "power2.out",
          scrollTrigger: {
            trigger: el,
            start: "top 60%",
            toggleActions: "play none none reverse",
          },
        });
      }
    },
    [isMobile],
  );

  return (
    <section ref={ref} className={styles.section}>
      <div data-rest-header>
        <h2 className={styles.title}>{t.aiTeam.restTitle}</h2>
        <p className={styles.desc}>{t.aiTeam.restDesc}</p>
        <div className={styles.aiLabel}>
          <span className={styles.aiDot} />
          {t.aiTeam.restAiLabel}
        </div>
      </div>

      <div className={styles.cardGrid}>
        {t.aiTeam.restSuggestions.map((suggestion, i) => (
          <div
            key={`${suggestion.player}-${i}`}
            className={`${styles.card} ${styles[`card${suggestion.type.charAt(0).toUpperCase()}${suggestion.type.slice(1)}`] || ""}`}
            data-rest-card
          >
            <div className={styles.cardHeader}>
              <span className={`${styles.badge} ${BADGE_CLASS[suggestion.type]}`} data-rest-badge>
                <span className={styles.badgeIcon}>{BADGE_ICON[suggestion.type]}</span>
                {badgeLabelMap[suggestion.type]}
              </span>
              <span className={styles.cardPlayer}>{suggestion.player}</span>
            </div>
            <p className={styles.cardText}>{suggestion.text}</p>
            <div className={styles.cardBar}>
              <div
                className={`${styles.cardBarFill} ${styles[`barFill${suggestion.type.charAt(0).toUpperCase()}${suggestion.type.slice(1)}`] || ""}`}
                data-rest-bar
              />
            </div>
          </div>
        ))}
      </div>
    </section>
  );
}
